import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Receipt, Plus, Layers, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_PAIEMENT = {
    impaye: { label: 'Impayée', cls: 'bg-red-100 text-red-700 border-red-200' },
    partiellement_paye: { label: 'Partielle', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
    totalement_paye: { label: 'Payée', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};
const TYPE_LABEL = {
    standard: { label: 'Prestation', cls: 'bg-slate-100 text-slate-700' },
    garde_meuble: { label: 'Garde-meuble', cls: 'bg-blue-100 text-blue-700' },
    consolidee: { label: 'Consolidée', cls: 'bg-violet-100 text-violet-700' },
};

function fmtEUR(n) {
    return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';
}
function fmtDate(d) {
    if (!d) return '—';
    try { return new Date(String(d).split('T')[0] + 'T00:00').toLocaleDateString('fr-FR'); } catch { return d; }
}

export default function FacturesSection({ clientId, hasGmActif }) {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gmOpen, setGmOpen] = useState(false);
    const [gmForm, setGmForm] = useState({ montant_ttc: '', date_facture: new Date().toISOString().slice(0, 10), date_echeance: '', remarque: '' });
    const [gmSaving, setGmSaving] = useState(false);
    const [consOpen, setConsOpen] = useState(false);
    const [consSelected, setConsSelected] = useState(new Set());
    const [consSaving, setConsSaving] = useState(false);

    const fetchFactures = async () => {
        try {
            const r = await axios.get(`${API_URL}/api/clients/${clientId}/factures`, { withCredentials: true });
            setFactures(r.data || []);
        } catch {
            toast.error('Erreur chargement factures');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clientId) fetchFactures();
    }, [clientId]);

    const submitGm = async () => {
        const montant = Number(gmForm.montant_ttc);
        if (!montant || montant <= 0) { toast.error('Montant invalide'); return; }
        setGmSaving(true);
        try {
            await axios.post(`${API_URL}/api/clients/${clientId}/factures/garde-meuble`,
                { montant_ttc: montant, date_facture: gmForm.date_facture, date_echeance: gmForm.date_echeance || null, remarque: gmForm.remarque },
                { withCredentials: true });
            toast.success('Facture GM créée');
            setGmOpen(false);
            setGmForm({ montant_ttc: '', date_facture: new Date().toISOString().slice(0, 10), date_echeance: '', remarque: '' });
            fetchFactures();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setGmSaving(false);
        }
    };

    const toggleConsSel = (id) => {
        setConsSelected(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const submitCons = async () => {
        if (consSelected.size < 2) { toast.error('Sélectionne au moins 2 factures'); return; }
        setConsSaving(true);
        try {
            const r = await axios.post(`${API_URL}/api/clients/${clientId}/factures/consolider`,
                { facture_ids: Array.from(consSelected) },
                { withCredentials: true });
            toast.success(`Facture consolidée ${r.data.numero} créée`);
            setConsOpen(false);
            setConsSelected(new Set());
            fetchFactures();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setConsSaving(false);
        }
    };

    const facturesGm = factures.filter(f => f.facture_type === 'garde_meuble');
    const consolidables = factures.filter(f => f.facture_type !== 'consolidee');

    return (
        <div className="bg-white rounded-xl border-2 border-[#1B365D]/20 shadow-sm p-6" data-testid="section-factures">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-[#1B365D]/10 flex items-center justify-center">
                        <Receipt className="w-4 h-4 text-[#1B365D]" />
                    </div>
                    <h2 className="text-base font-bold text-[#1B365D]">Factures</h2>
                    <Badge className="bg-[#1B365D]/10 text-[#1B365D] border-none text-xs">{factures.length}</Badge>
                </div>
                <div className="flex items-center gap-2">
                    {hasGmActif && (
                        <button
                            onClick={() => setGmOpen(true)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
                            data-testid="btn-add-facture-gm"
                        >
                            <Plus className="w-3.5 h-3.5" /> Ajouter facture GM
                        </button>
                    )}
                    {consolidables.length >= 2 && (
                        <button
                            onClick={() => { setConsSelected(new Set()); setConsOpen(true); }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 transition-colors"
                            data-testid="btn-total-factures"
                        >
                            <Layers className="w-3.5 h-3.5" /> Total des factures
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#1B365D]" /></div>
            ) : factures.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-4 text-center">Aucune facture</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50/70 text-[10px] uppercase text-gray-500 tracking-wide">
                            <tr>
                                <th className="text-left px-3 py-2">N°</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-left px-3 py-2">Désignation</th>
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-left px-3 py-2">Échéance</th>
                                <th className="text-right px-3 py-2">Prix TTC</th>
                                <th className="text-right px-3 py-2">Solde</th>
                                <th className="text-left px-3 py-2">Statut</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {factures.map(f => {
                                const t = TYPE_LABEL[f.facture_type] || TYPE_LABEL.standard;
                                const p = STATUT_PAIEMENT[f.statut_paiement] || STATUT_PAIEMENT.impaye;
                                return (
                                    <tr key={f.id}
                                        onClick={() => navigate(`/facturation/${f.id}`)}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        data-testid={`client-facture-${f.id}`}
                                    >
                                        <td className="px-3 py-2 font-mono text-[#1B365D]">{f.numero}</td>
                                        <td className="px-3 py-2"><Badge className={`text-[10px] ${t.cls} border-none`}>{t.label}</Badge></td>
                                        <td className="px-3 py-2 text-gray-700">{f.designation}</td>
                                        <td className="px-3 py-2 text-gray-600">{fmtDate(f.date_facture)}</td>
                                        <td className="px-3 py-2 text-gray-600">{fmtDate(f.date_echeance)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{fmtEUR(f.prix_ttc)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">
                                            <span className={f.solde > 0 ? 'text-red-600' : 'text-emerald-600'}>{fmtEUR(f.solde)}</span>
                                        </td>
                                        <td className="px-3 py-2"><Badge className={`text-[10px] ${p.cls} border`}>{p.label}</Badge></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Historique des factures GM (mini-rappel) */}
            {facturesGm.length > 0 && (
                <div className="mt-4 rounded-lg bg-blue-50/40 border border-blue-100 p-3">
                    <div className="flex items-center gap-2 text-blue-700 mb-2">
                        <FileText className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wide">Historique facturation garde-meuble</span>
                        <Badge className="bg-blue-100 text-blue-700 border-none text-[10px] ml-auto">{facturesGm.length} facture{facturesGm.length > 1 ? 's' : ''}</Badge>
                    </div>
                    <ul className="text-xs space-y-1">
                        {facturesGm.map(f => (
                            <li key={f.id} className="flex items-center justify-between">
                                <span className="text-gray-700">
                                    <span className="font-mono text-[#1B365D]">{f.numero}</span>
                                    <span className="mx-2 text-gray-400">·</span>
                                    {fmtDate(f.date_facture)}
                                    {f.date_echeance && <span className="text-gray-500"> (échéance {fmtDate(f.date_echeance)})</span>}
                                </span>
                                <span className="font-semibold">{fmtEUR(f.prix_ttc)}</span>
                            </li>
                        ))}
                        <li className="flex items-center justify-between pt-1 mt-1 border-t border-blue-100 font-semibold text-[#1B365D]">
                            <span>Total cumulé</span>
                            <span>{fmtEUR(facturesGm.reduce((s, f) => s + Number(f.prix_ttc || 0), 0))}</span>
                        </li>
                    </ul>
                </div>
            )}

            {/* Dialog GM */}
            <Dialog open={gmOpen} onOpenChange={setGmOpen}>
                <DialogContent data-testid="dialog-facture-gm">
                    <DialogHeader>
                        <DialogTitle>Nouvelle facture garde-meuble</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="gm-date">Date de la facture</Label>
                            <Input id="gm-date" type="date" value={gmForm.date_facture}
                                onChange={e => setGmForm({ ...gmForm, date_facture: e.target.value })}
                                data-testid="gm-date-facture" />
                        </div>
                        <div>
                            <Label htmlFor="gm-ech">Date d'échéance (optionnelle)</Label>
                            <Input id="gm-ech" type="date" value={gmForm.date_echeance}
                                onChange={e => setGmForm({ ...gmForm, date_echeance: e.target.value })}
                                data-testid="gm-date-echeance" />
                        </div>
                        <div>
                            <Label htmlFor="gm-montant">Montant TTC (€)</Label>
                            <Input id="gm-montant" type="number" step="0.01" min="0" value={gmForm.montant_ttc}
                                onChange={e => setGmForm({ ...gmForm, montant_ttc: e.target.value })}
                                placeholder="150.00" data-testid="gm-montant" />
                        </div>
                        <div>
                            <Label htmlFor="gm-rem">Remarque</Label>
                            <Textarea id="gm-rem" rows={2} value={gmForm.remarque}
                                onChange={e => setGmForm({ ...gmForm, remarque: e.target.value })}
                                placeholder="Ex: Stockage avril 2026" data-testid="gm-remarque" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setGmOpen(false)} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm">Annuler</button>
                        <button onClick={submitGm} disabled={gmSaving}
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                            data-testid="gm-submit">
                            {gmSaving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Créer la facture'}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Consolidation */}
            <Dialog open={consOpen} onOpenChange={setConsOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="dialog-consolidation">
                    <DialogHeader>
                        <DialogTitle>Jumeler des factures</DialogTitle>
                        <p className="text-xs text-gray-500 mt-1">
                            Coche les factures à regrouper. Une nouvelle facture consolidée sera créée ; les originales resteront visibles.
                        </p>
                    </DialogHeader>
                    <div className="space-y-1 max-h-80 overflow-y-auto">
                        {consolidables.length === 0 ? (
                            <p className="text-sm text-gray-400 italic p-3">Aucune facture à consolider</p>
                        ) : consolidables.map(f => {
                            const t = TYPE_LABEL[f.facture_type] || TYPE_LABEL.standard;
                            const checked = consSelected.has(f.id);
                            return (
                                <label
                                    key={f.id}
                                    onClick={(e) => { e.preventDefault(); toggleConsSel(f.id); }}
                                    className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-violet-300 bg-violet-50/50' : 'border-gray-200 hover:bg-gray-50'}`}
                                    data-testid={`cons-option-${f.id}`}
                                >
                                    <Checkbox checked={checked} onCheckedChange={() => toggleConsSel(f.id)} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs text-[#1B365D]">{f.numero}</span>
                                            <Badge className={`text-[9px] ${t.cls} border-none`}>{t.label}</Badge>
                                        </div>
                                        <p className="text-xs text-gray-500 truncate">{f.designation}</p>
                                    </div>
                                    <span className="text-sm font-semibold">{fmtEUR(f.prix_ttc)}</span>
                                </label>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-between text-sm font-semibold text-[#1B365D] px-2">
                        <span>{consSelected.size} sélectionnée{consSelected.size > 1 ? 's' : ''}</span>
                        <span>
                            Total : {fmtEUR(
                                consolidables
                                    .filter(f => consSelected.has(f.id))
                                    .reduce((s, f) => s + Number(f.prix_ttc || 0), 0)
                            )}
                        </span>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setConsOpen(false)} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm">Annuler</button>
                        <button onClick={submitCons} disabled={consSaving || consSelected.size < 2}
                            className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
                            data-testid="cons-submit">
                            {consSaving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Générer la facture consolidée'}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
